<script setup lang="ts">
import IconLogo from "@/assets/icons/IconLogo.vue";
import packageJson from "../../package.json";
import {ref} from "vue";
import IconPasswordVisible from "@/assets/icons/IconPasswordVisible.vue";
import IconPasswordHidden from "@/assets/icons/IconPasswordHidden.vue";
import {useRouter} from "vue-router";
import {LogIn} from "@/composables/auth";
import GlobalLoader from "@/components/ui/loaders/GlobalLoader.vue";
import {useStore} from "@/stores";

// Интерфейс формы пользователя
interface IUserForm {
  username: string;
  password: string;
}

// Интерфейс ошибок формы
interface IUserFormErrors {
  username: boolean;
  password: boolean;
}

const router = useRouter();
const store = useStore();

// Текущая версия приложения из package.json.
const version: string = packageJson.version;

// Флаг. Видимость пароля в поле ввода.
const isShowPassword = ref<boolean>(false);
const toggleShowPassword = (): void => {
  isShowPassword.value = !isShowPassword.value;
};

// Список ошибок
const userFormErrors = ref<IUserFormErrors>({
  username: false,
  password: false,
});

// Форма пользователя
const userForm = ref<IUserForm>({
  username: "",
  password: ""
});

// Отправка формы
const handleSubmit = async (event: Event): Promise<void> => {
  event.preventDefault();

  isAuthStart.value = true;

  // Сброс предыдущих ошибок
  userFormErrors.value.username = false;
  userFormErrors.value.password = false;

  // Валидация полей
  if (userForm.value.username.length === 0) {
    userFormErrors.value.username = true;
  }

  if (userForm.value.password.length === 0) {
    userFormErrors.value.password = true;
  }

  if (userFormErrors.value.username || userFormErrors.value.password) {
    isAuthStart.value = false;
    return;
  }

  // Выполнение авторизации
  const loginData = await LogIn(userForm.value);

  if (loginData.status) {
    await router.push("/map-view");
  } else {
    if (loginData.code === 401) {
      store.showWarning("Неверный логин или пароль", "Проверьте правильность ввода и повторите попытку", 15000);
    } else {
      store.showWarning("Сервер временно недоступен", "Повторите попытку позднее", 15000);
    }
  }

  isAuthStart.value = false;
};

// Статус логина.
const isAuthStart = ref(false);
</script>

<template>
  <main class="page__content">
    <div class="auth">
      <div class="auth__container">
        <div class="auth__header">
          <div class="auth__logo">
            <IconLogo />
          </div>

          <h1 class="auth__title">
            ОРАКУЛ — М
          </h1>

          <p class="auth__subtitle">
            Пожалуйста, авторизуйтесь, чтобы продолжить работу в системе
          </p>
        </div>

        <form
          class="auth__form form"
          role="form"
          aria-labelledby="auth-title"
          @submit="handleSubmit"
        >
          <InputPrimary
            v-model="userForm.username"
            label="Логин"
            :error="userFormErrors.username"
            aria-label="Введите логин"
          />
          <InputPrimary
            v-model="userForm.password"
            label="Пароль"
            :error="userFormErrors.password"
            :type="isShowPassword ? 'text' : 'password'"
            aria-label="Введите пароль"
          >
            <template #suffix>
              <IconPasswordVisible
                v-if="isShowPassword"
                role="button"
                class="password-icon"
                @click="toggleShowPassword"
              />
              <IconPasswordHidden
                v-else
                role="button"
                class="password-icon"
                @click="toggleShowPassword"
              />
            </template>
          </InputPrimary>
          <ButtonAccentPrimary
            size="large"
            type="submit"
            :pressed="isAuthStart"
          >
            Войти
          </ButtonAccentPrimary>
        </form>
      </div>
      <span class="auth__version">
        Версия {{ version }}
      </span>
    </div>
  </main>
  <GlobalLoader v-if="isAuthStart" />
</template>

<style scoped lang="scss">

.password-icon {
  width: 16px;
  height: 16px;
  cursor: pointer;
  user-select: none;
}

.page__content {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  width: 100%;
  background-color: #2D4B8E;
  overflow-y: auto;
}

.auth {
  display: flex;
  flex-direction: column;
  gap: 20px;
  background-color: var(--gray-gray30);
  padding: 10px 10px 20px 10px;
  border-radius: 30px;

  margin: 0 20px;
  max-width: 476px;

  &__header {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 16px;
  }

  &__title {
    font-weight: 700;
    font-size: 24px;
    line-height: 26px;
    text-transform: uppercase;
    color: var(--black-black100);
  }

  &__subtitle {
    font-weight: 400;
    font-size: 16px;
    line-height: 18px;
    color: var(--gray-gray80);
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 30px;
    text-align: left;
  }

  &__container {
    display: flex;
    flex-direction: column;
    gap: 27px;
    background-color: var(--background-bg-white);
    padding: 40px 30px 30px 30px;
    border-radius: 20px 20px 10px 10px;
  }

  &__version {
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: var(--gray-gray90);
    text-align: center;
  }
}


</style>